import React, { useEffect, useState, useRef } from 'react';
import API from '../api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './InvoicePage.css';

const InvoicePage = ({ createdInvoiceId, selectedService }) => {
  const [invoice, setInvoice] = useState(null);
  const [businessDetail, setBusinessDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const invoiceRef = useRef();

  const businessId = localStorage.getItem('id');

  // Handle missing invoice id (likely "error while creating invoice")
  useEffect(() => {
    if (!createdInvoiceId) {
      setLoading(false);
      setErrorMsg(
        'We were unable to create this invoice. Please try again or contact support if the issue persists.'
      );
    }
  }, [createdInvoiceId]);

  // Fetch invoice + business details
  useEffect(() => {
    if (!createdInvoiceId) return;

    const fetchData = async () => {
      try {
        const [invoiceRes, businessRes] = await Promise.all([
          API.get(`/auth/invoice/${createdInvoiceId}`),
          API.get(`/auth/business-detail?businessId=${businessId}`),
        ]);
        setInvoice(invoiceRes.data);
        setBusinessDetail(businessRes.data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load invoice:', err);
        setLoading(false);
        setErrorMsg(
          err?.response?.data?.error ||
            'Something went wrong while loading your invoice.'
        );
      }
    };

    fetchData();
  }, [createdInvoiceId, businessId]);

  /** Helpers **/

  const showError = (msg) => {
    setErrorMsg(msg || 'An unexpected error occurred.');
  };

  const closeError = () => {
    setErrorMsg('');
  };

 const printInvoice = () => {
  if (!invoiceRef.current) return;

  const printWindow = window.open('', 'PRINT', 'height=900,width=800');
  if (!printWindow) {
    showError('Pop-up blocked. Please allow pop-ups to print the invoice.');
    return;
  }

  const doc = printWindow.document;

  doc.write('<html><head><title>Invoice</title>');

  // Copy existing styles (both bundled <style> and linked CSS)
  const styleNodes = Array.from(
    document.querySelectorAll('style, link[rel="stylesheet"]')
  );
  styleNodes.forEach((node) => {
    doc.write(node.outerHTML);
  });

  // Print-specific adjustments so it looks like the screen design
  doc.write(`
    <style>
      @page {
        margin: 20mm;
      }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        background: #f5f7fb;
      }
      .invoice-page-root {
        padding: 0;
        background: transparent;
      }
      .invoice-card {
        margin: 0 auto;
        box-shadow: none !important;
        border-radius: 12px;
      }
      .invoice-actions {
        display: none !important;
      }
    </style>
  `);

  doc.write('</head><body>');
  doc.write('<div class="invoice-page-root">');
  // Only print the main invoice card referenced by invoiceRef
  doc.write(invoiceRef.current.outerHTML);
  doc.write('</div>');
  doc.write('</body></html>');

  doc.close();

  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
};
  const sendInvoice = async () => {
    if (!createdInvoiceId) {
      showError('Cannot send invoice: missing invoice ID.');
      return;
    }

    try {
      const res = await API.post(`/auth/invoice/${createdInvoiceId}/send`);
      if (res.status === 200) {
        alert('✅ Invoice sent successfully!');
      } else {
        showError('Unexpected response from server while sending invoice.');
      }
    } catch (err) {
      console.error('Error sending invoice:', err);
      const message =
        err?.response?.data?.error || 'Failed to send invoice.';
      showError(message);
    }
  };

  const generateAndSendPDF = async () => {
    if (!invoiceRef.current) return;

    try {
      const canvas = await html2canvas(invoiceRef.current);
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'pt', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      const pdfBlob = pdf.output('blob');

      const formData = new FormData();
      formData.append('file', pdfBlob, 'invoice.pdf');

      const customerEmail = invoice?.customerEmail;
      if (!customerEmail) {
        showError('Customer email is missing. Cannot send invoice.');
        return;
      }

      formData.append('email', customerEmail);

      const res = await API.post('/auth/invoice/send-pdf', formData);
      if (res.status === 200) {
        alert('✅ Invoice sent via email!');
      } else {
        showError('Server responded, but with an error while sending PDF.');
      }
    } catch (err) {
      console.error('Error sending invoice PDF:', err);
      showError('Failed to generate or send invoice PDF.');
    }
  };

  /** Totals **/

  const calculateMiscTotal = () =>
    (invoice?.miscItems ?? []).reduce(
      (sum, item) => sum + (item.price ?? 0),
      0
    );

  if (loading) {
    return (
      <div className="invoice-page-root">
        <div className="invoice-loading">Loading invoice...</div>
        {errorMsg && (
          <div className="invoice-error-overlay">
            <div className="invoice-error-modal">
              <div className="invoice-error-title">Invoice Error</div>
              <p className="invoice-error-body">{errorMsg}</p>
              <button
                className="invoice-error-close-btn"
                onClick={closeError}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // If loading is done but we still have no invoice, show popup + nothing else
  if (!invoice || !businessDetail) {
    return (
      <div className="invoice-page-root">
        <div className="invoice-loading">
          Invoice data is not available.
        </div>
        {errorMsg && (
          <div className="invoice-error-overlay">
            <div className="invoice-error-modal">
              <div className="invoice-error-title">Invoice Error</div>
              <p className="invoice-error-body">{errorMsg}</p>
              <button
                className="invoice-error-close-btn"
                onClick={closeError}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const miscTotal = calculateMiscTotal();
  const serviceTotal = invoice.serviceTotal ?? 0;
  const productsTotal = invoice.productsTotal ?? 0;
  const tipAmount = invoice.tipAmount ?? 0;
  const taxPercent = invoice.taxPercent ?? 0;
  const taxAmount =
    (taxPercent / 100) * (serviceTotal + productsTotal + miscTotal);
  const grandTotal =
    serviceTotal + productsTotal + miscTotal + tipAmount + taxAmount;

  // Build unified items list (service, product, misc)
  const items = [
    ...(selectedService
      ? [
          {
            key: 'svc-selected',
            type: 'Service',
            description: selectedService.name,
            quantity: 1,
            price: selectedService.price ?? 0,
            total: selectedService.price ?? 0,
          },
        ]
      : (invoice.services ?? []).map((svc, i) => ({
          key: `svc-${i}`,
          type: 'Service',
          description: svc.name,
          quantity: 1,
          price: svc.price ?? 0,
          total: svc.price ?? 0,
        }))),
    ...(invoice.products ?? []).map((p, i) => ({
      key: `prod-${i}`,
      type: 'Product',
      description: p.name,
      quantity: p.quantity ?? 0,
      price: p.price_per_unit ?? 0,
      total:
        p.total ?? (p.price_per_unit ?? 0) * (p.quantity ?? 0),
    })),
    ...(invoice.miscItems ?? []).map((m, i) => ({
      key: `misc-${i}`,
      type: 'Misc',
      description: m.name,
      quantity: 1,
      price: m.price ?? 0,
      total: m.price ?? 0,
    })),
  ];

  return (
    <div className="invoice-page-root">
      <div className="invoice-card" ref={invoiceRef}>
        {/* Header */}
        <div className="invoice-header">
          <div className="invoice-title-block">
            <h1>Invoice</h1>
            <div className="invoice-sub-label">
              Invoice Number: {invoice.invoiceNumber}
            </div>
          </div>

          <div className="invoice-meta">
            <div className="invoice-meta-row">
              <span>Issued:</span>
              <span>{invoice.issueDate}</span>
            </div>
            <div className="invoice-meta-row">
              <span>Due:</span>
              <span>{invoice.dueDate}</span>
            </div>
            <div className="invoice-meta-row">
              <span>Status:</span>
              <span className="invoice-status-pill">
                {invoice.status || 'Pending'}
              </span>
            </div>
          </div>
        </div>

        <hr className="invoice-divider" />

        {/* From / Bill To */}
        <div className="invoice-addresses">
          <div className="invoice-address-block">
            <div className="invoice-address-label">From</div>
            <div className="invoice-address-name">
              {businessDetail.owner?.firstName}{' '}
              {businessDetail.owner?.lastName}
            </div>
            <div>
              {businessDetail.owner?.address ||
                'Address not provided'}
            </div>
          </div>

          <div className="invoice-address-block">
            <div className="invoice-address-label">Bill To</div>
            <div className="invoice-address-name">
              {invoice.customerName || ''}
            </div>
            <div>{invoice.customerEmail || ''}</div>
          </div>
        </div>

        {/* Items */}
        <h3 className="invoice-section-title">Invoice Items</h3>
        <table className="invoice-items-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
              <th>QTY</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.key}>
                <td>{item.type}</td>
                <td>{item.description}</td>
                <td>{item.quantity}</td>
                <td>${item.price.toFixed(2)}</td>
                <td>${item.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="invoice-totals-wrapper">
          <div className="invoice-totals">
            <div className="invoice-totals-row">
              <span className="invoice-totals-label">
                Service Total:
              </span>
              <span className="invoice-totals-amount">
                ${serviceTotal.toFixed(2)}
              </span>
            </div>
            <div className="invoice-totals-row">
              <span className="invoice-totals-label">
                Product Total:
              </span>
              <span className="invoice-totals-amount">
                ${productsTotal.toFixed(2)}
              </span>
            </div>
            <div className="invoice-totals-row">
              <span className="invoice-totals-label">
                Misc Total:
              </span>
              <span className="invoice-totals-amount">
                ${miscTotal.toFixed(2)}
              </span>
            </div>
            <div className="invoice-totals-row">
              <span className="invoice-totals-label">
                Tax ({taxPercent}%):
              </span>
              <span className="invoice-totals-amount">
                ${taxAmount.toFixed(2)}
              </span>
            </div>
            <div className="invoice-totals-row">
              <span className="invoice-totals-label">Tip:</span>
              <span className="invoice-totals-amount">
                ${tipAmount.toFixed(2)}
              </span>
            </div>

            <div className="invoice-grand-total-label">
              Grand Total:
            </div>
            <div className="invoice-grand-total-amount">
              ${grandTotal.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="invoice-payment-method">
          <strong>Payment Method:</strong>{' '}
          {invoice.paymentMethod || '—'}
        </div>
      </div>

      {/* Actions (outside ref so not captured in PDF unless desired) */}
     <div className="invoice-card invoice-actions">
        <button
          className="invoice-btn invoice-btn-print"
          onClick={printInvoice}
        >
          <span className="invoice-btn-icon">🖨</span>
          <span>Print Invoice</span>
        </button>
        <button
          className="invoice-btn invoice-btn-send"
          onClick={generateAndSendPDF}
        >
          <span className="invoice-btn-icon">✉</span>
          <span>Send Email</span>
        </button>
      </div>

      {/* Error popup */}
      {errorMsg && (
        <div className="invoice-error-overlay">
          <div className="invoice-error-modal">
            <div className="invoice-error-title">Invoice Error</div>
            <p className="invoice-error-body">{errorMsg}</p>
            <button
              className="invoice-error-close-btn"
              onClick={closeError}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePage;
